import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addDigestFavourite,
  getDigestFavourite,
  getDigestFeed,
  getDigestMissed,
  getDigestSports,
  getDigestUpcoming,
  getDigestWatched,
  markDigestWatched,
  removeDigestFavourite,
  reportDigestBrokenLink,
  type SportDigestGroup,
} from '@nomiss/services';

export const digestQueryKeys = {
  all: ['digest'] as const,
  feed: (accessToken?: string | null) =>
    [...digestQueryKeys.all, 'feed', accessToken ?? ''] as const,
  upcoming: (accessToken?: string | null) =>
    [...digestQueryKeys.all, 'upcoming', accessToken ?? ''] as const,
  missed: (accessToken?: string | null) =>
    [...digestQueryKeys.all, 'missed', accessToken ?? ''] as const,
  watched: (accessToken?: string | null) =>
    [...digestQueryKeys.all, 'watched', accessToken ?? ''] as const,
  favourite: (accessToken?: string | null) =>
    [...digestQueryKeys.all, 'favourite', accessToken ?? ''] as const,
  sports: (accessToken?: string | null) =>
    [...digestQueryKeys.all, 'sports', accessToken ?? ''] as const,
};

/** Per-sport page size for the Home feed. Enough for a swipe deck without 100-item payloads. */
export const HOME_DIGEST_FEED_LIMIT = 20;

export const digestArchiveQueryKeyPrefixes = {
  missed: [...digestQueryKeys.all, 'missed'] as const,
  watched: [...digestQueryKeys.all, 'watched'] as const,
  favourite: [...digestQueryKeys.all, 'favourite'] as const,
};

export type DigestArchiveListKey = keyof typeof digestArchiveQueryKeyPrefixes;

export function invalidateDigestArchiveQueries(
  queryClient: { invalidateQueries: (filters: { queryKey: readonly unknown[] }) => unknown },
  keys: readonly DigestArchiveListKey[],
): void {
  for (const key of keys) {
    void queryClient.invalidateQueries({
      queryKey: digestArchiveQueryKeyPrefixes[key],
    });
  }
}

export function useDigestFeed(accessToken?: string | null, enabled = true) {
  return useQuery({
    queryKey: digestQueryKeys.feed(accessToken),
    queryFn: () => getDigestFeed({ accessToken, limit: HOME_DIGEST_FEED_LIMIT }),
    staleTime: 30_000,
    enabled: enabled && Boolean(accessToken),
  });
}

export function useDigestUpcoming(accessToken?: string | null, enabled = true) {
  return useQuery({
    queryKey: digestQueryKeys.upcoming(accessToken),
    queryFn: () => getDigestUpcoming({ accessToken }),
    staleTime: 30_000,
    enabled: enabled && Boolean(accessToken),
  });
}

export function useDigestMissed(accessToken?: string | null, enabled = true) {
  return useQuery({
    queryKey: digestQueryKeys.missed(accessToken),
    queryFn: () => getDigestMissed({ accessToken, limit: 100 }),
    staleTime: 30_000,
    enabled: enabled && Boolean(accessToken),
  });
}

export function useDigestWatched(accessToken?: string | null, enabled = true) {
  return useQuery({
    queryKey: digestQueryKeys.watched(accessToken),
    queryFn: () => getDigestWatched({ accessToken }),
    staleTime: 30_000,
    enabled: enabled && Boolean(accessToken),
  });
}

export function useDigestFavourite(accessToken?: string | null, enabled = true) {
  return useQuery({
    queryKey: digestQueryKeys.favourite(accessToken),
    queryFn: () => getDigestFavourite({ accessToken, limit: 100 }),
    staleTime: 30_000,
    enabled: enabled && Boolean(accessToken),
  });
}

export function useDigestSports(accessToken?: string | null, enabled = true) {
  return useQuery({
    queryKey: digestQueryKeys.sports(accessToken),
    queryFn: () => getDigestSports(accessToken),
    staleTime: 30_000,
    enabled: enabled && Boolean(accessToken),
  });
}

export function useMarkDigestWatched(accessToken?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (digestId: string) => markDigestWatched(digestId, accessToken),
    onSettled: () => {
      invalidateDigestArchiveQueries(queryClient, ['missed', 'watched']);
    },
  });
}

export function useReportDigestBrokenLink(accessToken?: string | null) {
  return useMutation({
    mutationFn: (vars: { digestId: string; brokenLink: string }) =>
      reportDigestBrokenLink(vars.digestId, vars.brokenLink, accessToken),
  });
}

function patchFavourited(
  groups: SportDigestGroup[] | undefined,
  digestId: string,
  isFavourited: boolean,
): SportDigestGroup[] | undefined {
  if (!groups) {
    return groups;
  }

  return groups.map(group => ({
    ...group,
    digests: group.digests.map(digest =>
      digest.id === digestId ? { ...digest, isFavourited } : digest,
    ),
  }));
}

function findDigestInGroups(
  groups: readonly SportDigestGroup[],
  digestId: string,
): { group: SportDigestGroup; digest: SportDigestGroup['digests'][number] } | undefined {
  for (const group of groups) {
    const digest = group.digests.find(item => item.id === digestId);
    if (digest) {
      return { group, digest };
    }
  }

  return undefined;
}

/**
 * Updates the Archive Favourites cache.
 * Favouriting inserts the digest from another list (Home feed, missed, …)
 * because a flag patch cannot add a card that was never in this list.
 */
export function patchFavouriteList(
  groups: SportDigestGroup[] | undefined,
  digestId: string,
  isFavourited: boolean,
  sourceGroups: readonly SportDigestGroup[] = [],
): SportDigestGroup[] | undefined {
  if (!groups) {
    return groups;
  }

  if (!isFavourited) {
    return groups
      .map(group => ({
        ...group,
        digests: group.digests.filter(digest => digest.id !== digestId),
      }))
      .filter(group => group.digests.length > 0);
  }

  if (groups.some(group => group.digests.some(digest => digest.id === digestId))) {
    return patchFavourited(groups, digestId, true);
  }

  const found = findDigestInGroups(sourceGroups, digestId);
  if (!found) {
    return groups;
  }

  const favouritedDigest = { ...found.digest, isFavourited: true };
  const existingGroup = groups.find(group => group.id === found.group.id);
  if (existingGroup) {
    return groups.map(group =>
      group.id === found.group.id
        ? { ...group, digests: [favouritedDigest, ...group.digests] }
        : group,
    );
  }

  return [
    {
      ...found.group,
      digests: [favouritedDigest],
    },
    ...groups,
  ];
}

const LIST_CACHE_KEYS = ['feed', 'upcoming', 'missed', 'watched', 'favourite'] as const;

function collectSourceGroups(
  previousByKey: readonly {
    key: (typeof LIST_CACHE_KEYS)[number];
    entries: readonly [unknown, SportDigestGroup[] | undefined][];
  }[],
): SportDigestGroup[] {
  const sourceGroups: SportDigestGroup[] = [];

  for (const { key, entries } of previousByKey) {
    if (key === 'favourite') {
      continue;
    }

    for (const [, data] of entries) {
      if (data) {
        sourceGroups.push(...data);
      }
    }
  }

  return sourceGroups;
}

export function useToggleDigestFavourite(accessToken?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { digestId: string; isFavourited: boolean }) =>
      vars.isFavourited
        ? removeDigestFavourite(vars.digestId, accessToken)
        : addDigestFavourite(vars.digestId, accessToken),
    onMutate: async vars => {
      await queryClient.cancelQueries({ queryKey: digestQueryKeys.all });

      const nextFavourited = !vars.isFavourited;
      const previousByKey = LIST_CACHE_KEYS.map(key => ({
        key,
        entries: queryClient.getQueriesData<SportDigestGroup[]>({
          queryKey: [...digestQueryKeys.all, key],
        }),
      }));
      const sourceGroups = collectSourceGroups(previousByKey);

      for (const { key } of previousByKey) {
        if (key === 'favourite') {
          queryClient.setQueriesData<SportDigestGroup[]>(
            { queryKey: [...digestQueryKeys.all, 'favourite'] },
            data =>
              patchFavouriteList(data, vars.digestId, nextFavourited, sourceGroups),
          );
        } else {
          queryClient.setQueriesData<SportDigestGroup[]>(
            { queryKey: [...digestQueryKeys.all, key] },
            data => patchFavourited(data, vars.digestId, nextFavourited),
          );
        }
      }

      return { previousByKey };
    },
    onError: (_error, _vars, context) => {
      if (!context) {
        return;
      }

      for (const { entries } of context.previousByKey) {
        for (const [key, data] of entries) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      invalidateDigestArchiveQueries(queryClient, ['favourite']);
    },
  });
}
